import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  EditProfileFormData,
  editProfileSchema,
} from "../../schemas/profileSchema";
import { User } from "../../types/auth";
import { FieldError } from "react-hook-form";

interface EditProfileFormProps {
  initialData?: User;
  onSubmit: (data: EditProfileFormData) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

const EditProfileForm: React.FC<EditProfileFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      username: initialData?.username || "",
      email: initialData?.email || "",
      phoneNumber: initialData?.phoneNumber || "",
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        placeholder="Enter your name"
        name="name"
        value={watch("name") || ""}
        onChange={(e) =>
          setValue("name", e.target.value, { shouldValidate: false })
        }
        onBlur={() => trigger("name")}
        error={errors.name as FieldError | undefined}
      />
      <Input
        label="Username"
        placeholder="Enter your username"
        name="username"
        value={watch("username") || ""}
        onChange={(e) =>
          setValue("username", e.target.value, { shouldValidate: false })
        }
        onBlur={() => trigger("username")}
        error={errors.username as FieldError | undefined}
      />
      <Input
        label="Email"
        placeholder="Enter your email"
        name="email"
        type="email"
        value={watch("email") || ""}
        onChange={(e) =>
          setValue("email", e.target.value, { shouldValidate: false })
        }
        onBlur={() => trigger("email")}
        error={errors.email as FieldError | undefined}
      />
      <Input
        label="Phone Number"
        placeholder="Enter your phone number"
        name="phoneNumber"
        value={watch("phoneNumber") || ""}
        onChange={(e) =>
          setValue("phoneNumber", e.target.value, { shouldValidate: false })
        }
        onBlur={() => trigger("phoneNumber")}
        error={errors.phoneNumber as FieldError | undefined}
        allowedChars="numeric"
      />
      <div className="flex justify-end space-x-4">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          Update Profile
        </Button>
      </div>
    </form>
  );
};

export default EditProfileForm;
